import { useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle } from 'lucide-react'
import { FormInput, FormPasswordInput, SubmitButton } from '@/components/form'
import { AuthCardLayout } from '@/components/AuthCardLayout'
import { useAuth } from '@/contexts/authContext'
import { authService } from '@/services/auth'
import { ROUTES } from '@/constants/routes'
import { queryKeys } from '@/lib/query-keys'
import { getApiErrorMessage } from '@/utils/errors'

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { refreshAuth } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const locationState = location.state as
    | { from?: { pathname: string }; message?: string }
    | undefined
  const from = locationState?.from?.pathname ?? ROUTES.DASHBOARD
  const successMessage = locationState?.message

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)
    try {
      const result = await authService.login({ username: email.trim(), password })
      if ('requires_2fa' in result && result.requires_2fa) {
        navigate(ROUTES.LOGIN_2FA, {
          replace: true,
          state: { temporaryToken: result.temporary_token, from },
        })
        return
      }
      queryClient.removeQueries({ queryKey: queryKeys.users.me })
      refreshAuth()
      navigate(from, { replace: true })
    } catch (err) {
      setError(getApiErrorMessage(err, t('login.error')))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthCardLayout title={t('login.title')}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {successMessage && (
          <div
            className="flex items-start gap-3 rounded-lg border border-green-300 bg-green-50 p-4"
            role="status"
          >
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
        )}
        {error && (
          <div
            className="mb-1 flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4"
            role="alert"
          >
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            <p className="text-sm font-normal text-red-600">{error}</p>
          </div>
        )}

        <FormInput
          id="login-email"
          label={t('login.emailLabel')}
          required
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder={t('login.emailPlaceholder')}
          autoComplete="email"
          disabled={isLoading}
        />

        <div>
          <FormPasswordInput
            id="login-password"
            label={t('login.passwordLabel')}
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder={t('login.passwordPlaceholder')}
            autoComplete="current-password"
            disabled={isLoading}
            showPasswordLabel={t('login.showPassword')}
            hidePasswordLabel={t('login.hidePassword')}
          />
          <Link
            to={ROUTES.FORGOT_PASSWORD}
            className="mt-2 inline-block text-sm text-blue-600 underline hover:opacity-90"
          >
            {t('login.forgotPassword')}
          </Link>
        </div>

        <SubmitButton
          fullWidth
          showIcon={false}
          isLoading={isLoading}
          loadingLabel={t('login.submitting')}
        >
          {t('login.submitButton')}
        </SubmitButton>
      </form>
    </AuthCardLayout>
  )
}
